import { useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Delete_Inventory, Edit_Inventory, Edit_Property_overview } from '../Edit_Propetry/Edit_Propetry_Api'
import Swal from 'sweetalert2'
import { propertyDetailsDataType } from '../PropertyDataTypes'

interface propsDataType {
  propertyDetails: propertyDetailsDataType
  id: string
  get_view_property: ()=> Promise<void>
}

interface initialValuesDataType {
  property_inventory_uuid: string
  inventory_item_name: string
  inventory_unit_set_number: string | number
  inventory_unit_set_name: string
  inventory_remarks: string
}

const PropertyInventory = ({ propertyDetails, id, get_view_property }: propsDataType) => {
  const initialValues = {
    property_inventory_uuid: '',
    inventory_item_name: '',
    inventory_unit_set_number: '',
    inventory_unit_set_name: '',
    inventory_remarks: '',
  }

  const formRef = useRef(null)
  const [isEditView, setEditView] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [inventoryInitialValues, setInventoryInitialValues] = useState<initialValuesDataType>(initialValues)

  const inventoryDetails:propertyDetailsDataType["inventoryDetails"] = propertyDetails?.inventoryDetails

  const validationSchema = Yup.object().shape({
    inventory_item_name: Yup.string()
      .required('Item name is required')
      .min(3, 'Minimum 3 characters required')
      .max(200, 'Maximum 200 characters are allowed'),
    inventory_unit_set_name: Yup.string()
      .required('Unit/set is required')
      .min(3, 'Minimum 3 characters required')
      .max(200, 'Maximum 200 characters are allowed'),
    inventory_unit_set_number: Yup.number()
      .typeError('Item count must be a number')
      .positive('Item count must be a positive number')
      .integer('Item count must be an integer')
      .required('Item count is required'),
    inventory_remarks: Yup.string()
      .optional()
      .min(3, 'Minimum 3 characters required')
      .max(650, 'Maximum 650 characters are allowed'),
  })

  const formik = useFormik({
    initialValues: inventoryInitialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => addInventoryToProperty(values),
  })

  const addInventoryToProperty = (values: initialValuesDataType) => {
    formik.validateForm().then(async (error: any) => {
      if (Object.keys(error).length === 0) {
        setLoading(true)
        if (inventoryInitialValues.property_inventory_uuid === '') {
          try {
            const { data: res } = await Edit_Property_overview(
              propertyDetails.property_uuid,
              propertyDetails.property_type_uuid,
              propertyDetails.property_title,
              propertyDetails.carpet_area,
              propertyDetails.carpet_area_unit_uuid,
              propertyDetails.address_1,
              propertyDetails.address_2,
              propertyDetails.country_id,
              propertyDetails.province_id,
              propertyDetails.city,
              propertyDetails.pin_zip,
              propertyDetails.current_rent,
              propertyDetails.account_kyc_uuid,
              propertyDetails.sub_unit_available,
              propertyDetails.property_usage_type,
              propertyDetails.property_furnishing_status,
              propertyDetails.sub_units,
              [
                ...propertyDetails?.inventoryDetails,
                {
                  property_inventory_uuid: '',
                  inventory_item_name: values.inventory_item_name,
                  inventory_unit_set_number: Number(values.inventory_unit_set_number),
                  inventory_unit_set_name: values.inventory_unit_set_name,
                  inventory_remarks: values.inventory_remarks,
                },
              ]
            )
            if (res.code === '200') {
              setLoading(false)
              setEditView(false)
              setInventoryInitialValues(initialValues)
              Swal.fire({
                position: 'center',
                icon: 'success',
                title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
                text: res.msg_details,
                allowOutsideClick: true,
              })
              get_view_property()
              formik.resetForm()
            } else {
              setLoading(false)
              Swal.fire({
                position: 'center',
                icon: 'error',
                title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
                text: res.msg_details,
                allowOutsideClick: true,
              })
            }
          }
          catch (error: any) {
            Swal.fire({
              position: 'center',
              icon: 'error',
              title: 'Error',
              text: error,
              allowOutsideClick: true,
            })
          }
        }
        else {
          try {
            const { data: res } = await Edit_Inventory(
              values.inventory_item_name,
              values.inventory_remarks,
              values.inventory_unit_set_name,
              Number(values.inventory_unit_set_number),
              values.property_inventory_uuid
            )
            if (res.code === '200') {
              setEditView(false)
              setInventoryInitialValues(initialValues)
              get_view_property()
              Swal.fire({
                position: 'center',
                icon: 'success',
                title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
                text: res.msg_details,
                allowOutsideClick: true,
              })
            } else {
              Swal.fire({
                position: 'center',
                icon: 'error',
                title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
                text: res.msg_details,
                allowOutsideClick: true,
              })
            }
          }
          catch (error: any) {
            Swal.fire({
              position: 'center',
              icon: 'error',
              title: 'Error',
              text: error,
              allowOutsideClick: true,
            })
          }
          finally {
            setLoading(false)
          }
        }
      } else {
        const firstErrorField = Object.keys(error)[0]
        if (firstErrorField) {
          // @ts-ignore
          const fieldElement = formRef.current?.elements[firstErrorField] as HTMLElement | undefined
          if (fieldElement) {
            fieldElement.scrollIntoView({ behavior: 'smooth' })
          }
        }
      }
    })
  }

  const handleEditInventory = (item: initialValuesDataType) => {
    setInventoryInitialValues(item)
    setEditView(true)
  }

  const handleDeleteInventory = async (item: initialValuesDataType) => {
    Swal.fire({
      title: 'Are you sure you want to archive this inventory?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const { data: res } = await Delete_Inventory(
            item.property_inventory_uuid
          )
          if (res.code === '200') {
            Swal.fire({
              position: 'center',
              icon: 'success',
              title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
              text: res.msg_details,
              allowOutsideClick: true,
            })
            get_view_property()
          } else {
            Swal.fire({
              position: 'center',
              icon: 'error',
              title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
              text: res.msg_details,
              allowOutsideClick: true,
            })
          }
        }
        catch (error: any) {
          Swal.fire({
            position: 'center',
            icon: 'error',
            title: 'Error',
            text: error,
            allowOutsideClick: true,
          })
        }
      }
    })
  }

  return (
    <div className='card mb-6 mb-xl-10'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <div className='card-title m-0'>
          <h3 className='fw-bold m-0'>Inventory</h3>
        </div>
        <div className='d-flex flex-column pt-3'>
          {isEditView ? (
            <div className='align-self-center'>
              <button
                className='btn btn-sm btn-light fw-bold bg-color-white shadow me-4 btn-active-light-primary'
                onClick={() => {
                  setEditView(false)
                  setInventoryInitialValues(initialValues)
                  formik.resetForm()
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className='align-self-center btn btn-sm btn-primary me-3'
              onClick={() => setEditView(true)}
            >
              Add Inventory Item
            </button>
          )}
        </div>
      </div>

      {isEditView ? (
        <div id='kt_account_profile_details' className='collapse show'>
          <form ref={formRef} onSubmit={formik.handleSubmit} className='form'>
            <div className='card-body border-top p-9'>
              <div className='row'>
                <div
                  className='col-lg-6 col-12'
                >
                  <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                    <span className='required'>Item Name</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter item name'
                    ></i>
                  </label>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    {...formik.getFieldProps('inventory_item_name')}
                    name='inventory_item_name'
                    placeholder='Enter item name'
                  />
                  <div className='h-20px'>
                    {formik.touched.inventory_item_name && formik.errors.inventory_item_name && (
                      <div className='fv-help-block color-red'>
                        {formik.errors.inventory_item_name}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className='col-lg-6 col-12'
                >
                  <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                    <span className='required'>Number of items</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Enter item name'
                    ></i>
                  </label>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    {...formik.getFieldProps('inventory_unit_set_number')}
                    name='inventory_unit_set_number'
                    placeholder='Enter number of items'
                  />
                  <div className='h-20px'>
                    {formik.touched.inventory_unit_set_number &&
                      formik.errors.inventory_unit_set_number && (
                        <div className='fv-help-block color-red'>
                          {formik.errors.inventory_unit_set_number}
                        </div>
                      )}
                  </div>
                </div >
              </div>
              <div className='row'>
                <div
                  className='col-lg-6 col-12'
                >
                  <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                    <span className='required'>Unit/Set</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Choose unit/set'
                    ></i>
                  </label>
                  <select
                    className='form-select form-select-solid'
                    {...formik.getFieldProps('inventory_unit_set_name')}
                    name='inventory_unit_set_name'
                    placeholder='Select Unit/Set'
                  >
                    <option className='bg-color-gray' value='' disabled>
                      Select Unit/Set
                    </option>
                    <option value='unit'>Unit</option>
                    <option value='set'>Set</option>
                  </select>
                  <div className='h-20px'>
                    {formik.touched.inventory_unit_set_name &&
                      formik.errors.inventory_unit_set_name && (
                        <div className='fv-help-block color-red'>
                          {formik.errors.inventory_unit_set_name}
                        </div>
                      )}
                  </div>
                </div>
                <div
                  className='col-lg-6 col-12'
                >
                  <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                    <span className='required'>Remarks</span>
                    <i
                      className='fas fa-exclamation-circle ms-2 fs-7'
                      data-bs-toggle='tooltip'
                      title='Remarks'
                    ></i>
                  </label>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    {...formik.getFieldProps('inventory_remarks')}
                    name='inventory_remarks'
                    placeholder='Enter remarks'
                  />
                  <div className='h-20px'>
                    {formik.touched.inventory_remarks && formik.errors.inventory_remarks && (
                      <div className='fv-help-block color-red'>
                        {formik.errors.inventory_remarks}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className='d-flex flex-column align-items-center'>
                <button
                  type='submit'
                  className='btn btn-sm btn-primary shadow mt-3'
                  style={{ cursor: 'pointer' }}
                  disabled={loading}
                >
                  {loading ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  ) : inventoryInitialValues.property_inventory_uuid === '' ? (
                    <span>Add Inventory</span>
                  ) : (
                    <span>Edit Inventory</span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      ) : inventoryDetails?.length === 0 ? (
        <div className='d-flex flex-column align-items-center flex-start p-8'>
          <img
            src='/media/svg/no-data-found/no-data-found.svg'
            alt='No document uploaded'
            className='h-200px w-200px'
          />
          <span className='fw-bold text-muted fs-1 text-center'>No inventory added</span>
        </div>
      ) : (
        // <div className='d-flex flex-wrap g-3 g-xl-5 mb-1 mb-xl-3 mt-8'>
        <div className='row px-5 py-3'>
          {/* <div className=''> */}
          {inventoryDetails?.map((item) => {
            return (
              <div className='col-lg-4 col-12 rounded p-2' key={item.property_inventory_uuid}>
                <div
                  className='image-container w-100 shadow-sm'
                >
                  <div className='row w-100 image p-4'>
                    <div className='d-flex col-9'>
                      <span className='bullet bullet-vertical h-40px bg-primary'></span>

                      <div style={{ marginLeft: 20 }} className='flex-grow-1'>
                        <span className='text-gray-800 text-hover-primary fw-bold fs-6'>
                          {item.inventory_item_name}
                        </span>
                        <span className='text-muted fw-semibold d-block'>
                          Remarks :{' '}
                          {item.inventory_remarks.length > 0 ? item.inventory_remarks : 'NA'}
                        </span>
                      </div>
                    </div>
                    <div
                      style={{ maxHeight: "40px" }}
                      className='badge badge-light-primary fw-bold p-3 col-3'
                    >
                      {item.inventory_unit_set_number} {item.inventory_unit_set_name.charAt(0).toUpperCase() + item.inventory_unit_set_name.slice(1)}
                    </div>
                  </div>

                  <div className='row w-100 m-0 justify-content-center'>
                    <button
                      className='border border-secondary p-0 h-30px btn btn-light btn-sm col-6 rounded-0 rounded-bottom image_icon'
                      title='Edit'
                      onClick={() => handleEditInventory(item)}
                    >
                      <i className='bi bi-pencil-square text-primary fa-md fs-2'></i>
                      <span>Edit</span>
                    </button>
                    <button
                      className='border border-secondary p-0 h-30px btn btn-light btn-sm col-6 rounded-0 rounded-bottom image_icon'
                      title='Archive'
                      onClick={() => handleDeleteInventory(item)}
                    >
                      <i className='bi bi-trash text-danger fa-md fs-2'></i>
                      <span>Archive</span>
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
          {/* </div> */}
        </div>
        // </div>
      )}
    </div>
  )
}

export default PropertyInventory
